import { franc } from 'franc';
import moment from 'moment';
import {
  DUBBING_LENGTH_LIMIT,
  DUB_FILE_FORMAT,
  ERROR_SEPARATOR,
  ERROR_SEPARATOR_PROJECT,
  ERROR_TYPES,
  MAX_SENTENCE_LENGTH,
  SUBTITLES_S3_DIRECTORY,
} from '@src/constants/dubbing';
import {
  validateSRTDuration,
  validateDubbingBlocks,
} from '@vbee-holding/aivoice-shared-lib';
import { VALID_CHARACTERS_LENGTH_REGEX } from '@src/constants/tts';
import { FRANC_LANG_VN } from '@src/constants/upload';
import { isValidFile } from '@src/utils/checkValid';
import { createFileFromText } from '@src/utils/file';
import {
  getSRTDuration,
  parseSRTFile,
  parseSRTFileProject,
} from '@src/utils/srt';
import { uploadToS3FromFile } from './upload';

const getInvalidTextsErrorMessage = (content, invalidTexts) => {
  let errorMsg = content;
  invalidTexts.forEach((invalidText) => {
    errorMsg = errorMsg.replace(
      invalidText,
      `<span style="color: red">${invalidText}</span>`,
    );
  });
  return errorMsg;
};

const validateSrtProject = async (srtFile) => {
  if (!srtFile) {
    return { error: true, message: 'fileCannotBeEmpty' };
  }

  if (!isValidFile(srtFile.name, DUB_FILE_FORMAT)) {
    return { error: true, message: 'fileFormatError' };
  }

  try {
    const { parsedSubtitles } = await parseSRTFileProject(srtFile);
    if (!parsedSubtitles?.length) {
      throw new Error('novalidContentSrtFile');
    }

    const subtitlesForValidation = parsedSubtitles.map(
      ({ start, end, content }) => ({
        startTime: start,
        endTime: end,
        text: content,
      }),
    );

    // Validate timing
    const blockTimingValidation = validateDubbingBlocks(subtitlesForValidation);
    if (!blockTimingValidation.isValid) {
      const errors = blockTimingValidation?.errors || [];

      const errorsInvalidTimeFormat = errors.filter(
        (block) => block?.errorCode === 'INVALID_TIME_FORMAT',
      );
      if (errorsInvalidTimeFormat.length) {
        const err = new Error('invalidTimeDeltaFormat');
        err.detail = errorsInvalidTimeFormat
          .map(
            ({ blockIndex, block }) =>
              `<div style="color: red"><div style="color: black">${
                blockIndex + 1
              }</div>${block?.startTime} --> ${
                block?.endTime
              }<br/><span style="color: black">${block?.text}</span></div>`,
          )
          .join(ERROR_SEPARATOR_PROJECT);
        throw err;
      }

      const errorsStartGreaterThanEnd = errors.filter(
        (block) => block?.errorCode === 'START_TIME_GREATER_THAN_END_TIME',
      );
      if (errorsStartGreaterThanEnd.length) {
        const err = new Error('startTimeMustBeLessThanEndTime');
        err.detail = errorsStartGreaterThanEnd
          .map(
            ({ blockIndex, block }) =>
              `<div style="color: red"><div style="color: black">${
                blockIndex + 1
              }</div>${block?.startTime} --> ${
                block?.endTime
              }<br/><span style="color: black">${block?.text}</span></div>`,
          )
          .join(ERROR_SEPARATOR_PROJECT);
        throw err;
      }

      const errorsBlockOverlap = errors.filter(
        (block) => block?.errorCode === 'BLOCKS_OVERLAP',
      );
      if (errorsBlockOverlap?.length) {
        const err = new Error(
          'startTimeMustBeGreaterOrEqualThanPrevBlockEndTime',
        );
        err.detail = errorsBlockOverlap
          .map(({ blockIndex, block }) => {
            const prevBlock = parsedSubtitles[blockIndex - 1];
            return `<div style="color: red"><div style="color: black">${prevBlock?.id}</div>${prevBlock?.start} --> ${prevBlock?.end}<br/><span style="color: black">${prevBlock?.content}</span></div>
            <br/>
            <div style="color: red"><div style="color: black">${parsedSubtitles[blockIndex]?.id}</div>${block?.startTime} --> ${block?.endTime}<br/><span style="color: black">${block?.text}</span></div>`;
          })
          .join(ERROR_SEPARATOR_PROJECT);
        throw err;
      }

      const errorsTextTooLong = errors.filter(
        (block) => block?.errorCode === 'TEXT_TOO_LONG',
      );
      if (errorsTextTooLong?.length) {
        const err = new Error('sentenceTooLong');
        err.detail = errorsTextTooLong
          .map(
            ({ blockIndex, block }) =>
              `<div style="color: black"><div style="color: black">${
                blockIndex + 1
              }</div>${block?.startTime} --> ${
                block?.endTime
              }<br/><span style="color: red">${block?.text}</span></div>`,
          )
          .join(ERROR_SEPARATOR_PROJECT);
        throw err;
      }

      const errorsTextCharacterLengthTooLong = errors.filter(
        (block) => block?.errorCode === 'TEXT_CHARACTER_LENGTH_TOO_LONG',
      );

      const wordErrors = [];
      errorsTextCharacterLengthTooLong.forEach((subtitle) => {
        const { blockIndex, block } = subtitle || {};
        const { startTime, endTime, text } = block || {};
        const invalidTexts = text.match(VALID_CHARACTERS_LENGTH_REGEX);
        if (invalidTexts?.length) {
          const errorMessage = `<div>${
            blockIndex + 1
          }<br/>${startTime} --> ${endTime}<br/><span>${getInvalidTextsErrorMessage(
            text,
            invalidTexts,
          )}</span></div>`;
          wordErrors.push(errorMessage);
        }
      });
      if (wordErrors?.length) {
        const err = new Error('invalidCharacterLength');
        err.detail = wordErrors.join(ERROR_SEPARATOR);
        throw err;
      }
    }

    // Validate total audio duration
    const { isValid, duration } =
      validateSRTDuration(subtitlesForValidation) || {};
    if (!isValid) {
      throw new Error('audioLengthTooLong');
    }

    return {
      error: false,
      subtitles: parsedSubtitles,
      totalSeconds: duration / 1000,
    };
  } catch (error) {
    return { error: true, message: error.message, detail: error.detail };
  }
};

const validateSubtitleFile = async (srtFile, acceptMultiLanguage = false) => {
  if (!srtFile) return { error: true, message: 'fileCannotBeEmpty' };

  const isValidSrtFile = isValidFile(srtFile.name, DUB_FILE_FORMAT);
  if (!isValidSrtFile) return { error: true, message: 'fileFormatError' };

  try {
    const { error, message, detail, parsedSubtitles } = await parseSRTFile(
      srtFile,
    );

    if (message === ERROR_TYPES.INVALID_SRT_FORMAT)
      return { error: true, message, detail };

    if (!parsedSubtitles?.length) throw new Error('invalidSrtFormat');

    // Check language of subtitle file is vietnamese or not
    const subtitleContent = parsedSubtitles
      .map((subtitle) => subtitle.content)
      .join(' ');
    const detectLanguage = franc(subtitleContent);
    if (detectLanguage !== FRANC_LANG_VN && !acceptMultiLanguage) {
      throw new Error('subtitleLanguageNotVietnamese');
    }

    if (error) return { error, message, detail };

    // Check if total audio duration is more than dubbing audio limit hours
    const subtitleDuration = getSRTDuration(parsedSubtitles);
    const subtitleDurationTooLong =
      subtitleDuration > DUBBING_LENGTH_LIMIT * 3600 * 1000;
    if (subtitleDurationTooLong) throw new Error('audioLengthTooLong');

    // Check if every sentences is less than MAX_SENTENCE_LENGTH characters
    const errBlocks = parsedSubtitles.filter(
      (subtitle) => subtitle.content.length > MAX_SENTENCE_LENGTH,
    );
    if (errBlocks?.length) {
      const err = new Error('sentenceTooLong');
      err.detail = errBlocks
        .map((block) => {
          const { id, start, end, content } = block;
          return `<div>${id}<br/>${start} --> ${end}<br/><span style="color: red">${content}</span></div>`;
        })
        .join(ERROR_SEPARATOR);
      throw err;
    }

    // Check if all words in subtitle file have less than MAX_CHARACTER_LENGTH characters
    const wordErrors = [];
    parsedSubtitles.forEach((subtitle) => {
      const { id, start, end, content } = subtitle;
      const invalidTexts = content.match(VALID_CHARACTERS_LENGTH_REGEX);
      if (invalidTexts?.length) {
        const errorMessage = `<div>${id}<br/>${start} --> ${end}<br/><span>${getInvalidTextsErrorMessage(
          content,
          invalidTexts,
        )}</span></div>`;
        wordErrors.push(errorMessage);
      }
    });
    if (wordErrors?.length) {
      const err = new Error('invalidCharacterLength');
      err.detail = wordErrors.join(ERROR_SEPARATOR);
      throw err;
    }

    return {
      detectLanguage,
      error: false,
      subtitles: parsedSubtitles,
      totalSeconds: subtitleDuration / 1000,
    };
  } catch (error) {
    return { error: true, message: error.message, detail: error.detail };
  }
};

const createSRTFile = async (fileName, sentences) => {
  try {
    const subtitleBlocks = Object.values(sentences);
    const srtContent = subtitleBlocks.reduce((result, block) => {
      const { id, start, end, content } = block;
      return `${result}${id}\n${start} --> ${end}\n${content}\n\n`;
    }, '');
    const file = createFileFromText(fileName, srtContent);
    const fileUrl = await uploadToS3FromFile(file, SUBTITLES_S3_DIRECTORY);
    return { error: false, fileUrl };
  } catch (error) {
    return { error: true };
  }
};

const checkCreateSrtFileSuccess = async (fileName, sentences) => {
  try {
    const subtitleBlocks = Object.values(sentences);
    const srtContent = subtitleBlocks.reduce((result, block) => {
      const { id, start, end, content } = block;
      return `${result}${id}\n${start} --> ${end}\n${content}\n\n`;
    }, '');
    const file = createFileFromText(fileName, srtContent);
    const { error } = await validateSubtitleFile(file);
    return { error };
  } catch (error) {
    return { error: true };
  }
};

const calculateDubbingDuration = async (subtitleLink) => {
  const response = await fetch(subtitleLink);
  const text = await response.text();
  if (text.length === 0) return 0;
  const srtFile = createFileFromText('subtitle.srt', text);
  const parseResult = await parseSRTFile(srtFile);
  const subtitleDuration = getSRTDuration(parseResult?.parsedSubtitles);
  return subtitleDuration;
};

const convertMillisToDubbingDuration = (millis) => {
  const timeDuration = moment.duration(millis);
  const formatNumber = (number) => (number < 10 ? `0${number}` : number);

  const hours = formatNumber(Math.floor(timeDuration.asHours()));
  const minutes = formatNumber(timeDuration.minutes());
  const seconds = formatNumber(timeDuration.seconds());

  if (timeDuration.milliseconds() > 0) {
    return `${hours}:${minutes}:${formatNumber(timeDuration.seconds() + 1)}`;
  }

  return `${hours}:${minutes}:${seconds}`;
};

/**
 * The slot covering `seconds`, or null between slots. Ascending, keyed on
 * `startSec` / `endSec`. Runs per animation frame, so it stays O(log n).
 */
const findSlotAtSeconds = (slots, seconds) => {
  let low = 0;
  let high = slots.length - 1;
  let lastStartedIndex = -1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (slots[mid].startSec <= seconds) {
      lastStartedIndex = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  if (lastStartedIndex === -1) return null;
  const slot = slots[lastStartedIndex];
  return seconds < slot.endSec ? slot : null;
};

export {
  findSlotAtSeconds,
  validateSubtitleFile,
  createSRTFile,
  calculateDubbingDuration,
  convertMillisToDubbingDuration,
  checkCreateSrtFileSuccess,
  validateSrtProject,
};
